import { useCallback } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

/**
 * `useQueries` `combine` for load-more batches: each batch's data, whether a
 * batch without data is still loading or failed, and the refetches of the
 * failed ones.
 */
export function summarizeBatchResults<TResponse>(
    results: Array<UseQueryResult<TResponse>>,
) {
    const missingResults = results.filter(
        (result) => result.data === undefined,
    );
    return {
        responses: results.map((result) => result.data),
        placeholderFlags: results.map((result) => result.isPlaceholderData),
        isAnyBatchPending: missingResults.some((result) => !result.isError),
        isAnyBatchError: missingResults.some((result) => result.isError),
        failedRefetches: missingResults
            .filter((result) => result.isError)
            .map((result) => result.refetch),
    };
}

/** Retry callback that refetches every failed batch. */
export function useRetryFailedBatches(
    failedRefetches: Array<UseQueryResult["refetch"]>,
) {
    return useCallback(() => {
        for (const refetch of failedRefetches) {
            void refetch();
        }
    }, [failedRefetches]);
}
